import { AsyncPipe, CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { NgxTurnstileModule } from 'ngx-turnstile';
import { Subscription } from 'rxjs';
import { AfmBeService } from 'src/app/services/afm-be/afm-be.service';
import { AuthService } from 'src/app/services/auth-service/auth-service.service';
import { LoadingBarService } from 'src/app/services/loading-bar/loading-bar.service';
import { UserSettingsService } from 'src/app/services/user-settings/user-settings.service';
import { SectionComponent } from 'src/app/shared/components/section/section.component';
import { Stripe } from 'stripe';

@Component({
  selector: 'app-manage-subscription',
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule, MatCardModule, MatButtonModule, MatTooltipModule, SectionComponent, NgxTurnstileModule, AsyncPipe],
  templateUrl: './manage-subscription.component.html',
  styleUrls: ['./manage-subscription.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageSubscriptionComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  isLoadingPortal = signal<boolean>(false);
  showTurnstileOverlay = signal<boolean>(false);
  pendingAction: ((token: string) => void) | null = null;

  turnstileSiteKey = '0x4AAAAAAA177F80eXAbXgwO';

  constructor(
    public afmbe: AfmBeService,
    public authService: AuthService,
    public userSettingsService: UserSettingsService,
    public loadingBarService: LoadingBarService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  onTurnstileResponse(response: string | null) {
    if (response && this.pendingAction) {
      this.pendingAction(response);
      this.pendingAction = null;
    }
    setTimeout(() => {
      this.showTurnstileOverlay.set(false);
      this.cdr.markForCheck();
    }, 1000);
  }

  onTurnstileError(errorCode: string | null) {
    console.error('Turnstile error:', errorCode);
    setTimeout(() => {
      this.showTurnstileOverlay.set(false);
      this.pendingAction = null;
      this.cdr.markForCheck();
    }, 1500);
  }

  withTurnstileVerification(action: (token: string) => void): void {
    this.showTurnstileOverlay.set(true);
    this.pendingAction = action;
    this.cdr.markForCheck();
  }

  getPortalSession(customerId: string): void {
    this.withTurnstileVerification((token) => {
      this.isLoadingPortal.set(true);
      this.cdr.markForCheck();
      this.afmbe.createPortalSession(customerId, token).subscribe({
        next: (session) => {
          window.location.href = session.url ?? '';
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error(err);
          this.isLoadingPortal.set(false);
          this.cdr.markForCheck();
        },
      });
    });
  }

  getSubscriptionsForCustomer(customerId: string): Stripe.Subscription[] {
    return this.afmbe.afmStripeUserData().showableSubscriptions.filter((sub) => {
      const customer = this.asCustomer(sub.customer);
      return customer?.id === customerId;
    });
  }

  getInitials(name: string): string {
    return name
      .split(' ')
      .map((n) => n[0] + '.')
      .join(' ')
      .toUpperCase();
  }

  privatizeEmail(email: string): string {
    let [name, domain] = email.split('@');
    return `${name[0]}***${name[name.length - 1]}@${domain}`;
  }

  asCustomer(value: string | Stripe.Customer | Stripe.DeletedCustomer): Stripe.Customer | null {
    if (typeof value !== 'string' && 'id' in value) {
      return value as Stripe.Customer;
    }
    return null;
  }
}
